import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Camera, Plus, Save, Loader2, AlertCircle, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { petsService, CreatePetData } from '../../lib/petsService';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

interface AddPetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const speciesOptions = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  { value: 'bird', label: 'Bird' },
  { value: 'fish', label: 'Fish' },
  { value: 'rabbit', label: 'Rabbit' },
  { value: 'hamster', label: 'Hamster' },
  { value: 'guinea_pig', label: 'Guinea Pig' },
  { value: 'ferret', label: 'Ferret' },
  { value: 'horse', label: 'Horse' },
  { value: 'reptile', label: 'Reptile' },
  { value: 'other', label: 'Other' }
];

export function AddPetModal({ isOpen, onClose, onSuccess }: AddPetModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<CreatePetData>({
    name: '',
    species: '',
    breed: '',
    birth_date: '',
    weight: undefined,
    color: '',
    microchip_number: '',
    registration_number: '',
    notes: '',
    guardian_name: '',
    guardian_relationship: '',
    guardian_phone: '',
    guardian_email: '',
    guardian_address: '',
    guardian_notes: ''
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field: keyof CreatePetData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Please select a valid image file' }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
      return;
    }

    setImageFile(file);
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.image;
      return newErrors;
    });

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Pet name is required';
    }

    if (!formData.species) {
      newErrors.species = 'Species is required';
    }

    if (formData.weight !== undefined && formData.weight < 0) {
      newErrors.weight = 'Weight must be a positive number';
    }

    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      if (birthDate > today) {
        newErrors.birth_date = 'Birth date cannot be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      species: '',
      breed: '',
      birth_date: '',
      weight: undefined,
      color: '',
      microchip_number: '',
      registration_number: '',
      notes: '',
      guardian_name: '',
      guardian_relationship: '',
      guardian_phone: '',
      guardian_email: '',
      guardian_address: '',
      guardian_notes: ''
    });
    setImageFile(null);
    setImagePreview(null);
    setErrors({});
    setSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setErrors({});
    try {
      const petData: CreatePetData = { ...formData };
      // Clean undefined and empty string values to avoid DB constraint issues
      (Object.keys(petData) as (keyof CreatePetData)[]).forEach(key => {
        if (petData[key] === '' || petData[key] === undefined) {
          delete petData[key];
        }
      });

      const createdPet = await petsService.createPet(petData);

      if (imageFile && createdPet) {
        try {
          const imageUrl = await petsService.uploadPetImage(imageFile, createdPet.id);
          await petsService.updatePet(createdPet.id, { image_url: imageUrl });
        } catch (imageError) {
          console.error('Failed to upload image:', imageError);
        }
      }

      onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error adding pet:', err);
      setErrors({ submit: err instanceof Error ? err.message : 'Failed to add pet. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Add New Pet</h2>
              <p className="text-gray-600 mt-1">Enter your pet's information</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </Button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Success Message */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <CheckCircle size={20} className="text-green-600 mr-3" />
                <p className="text-green-800 font-medium">Pet created successfully!</p>
              </motion.div>
            )}

            {/* Error Message */}
            {errors.submit && (
              <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={20} className="text-red-600 mr-3" />
                <p className="text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Image Upload Section */}
            <div className="space-y-4">
              <Label className="text-base font-semibold text-gray-900">Pet Photo</Label>
              <div className="flex items-center space-x-4">
                {/* Image Preview */}
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Pet preview"
                      className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white hover:bg-red-600 w-6 h-6 rounded-full"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ) : (
                  <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <ImageIcon size={32} className="text-gray-400" />
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full"
                  >
                    <Upload size={16} className="mr-2" />
                    Upload Photo
                  </Button>
                  {errors.image && (
                    <p className="text-red-600 text-sm mt-1">{errors.image}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                  Pet Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter pet name"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="text-red-600 text-sm">{errors.name}</p>
                )}
              </div>

              {/* Species */}
              <div className="space-y-2">
                <Label htmlFor="species" className="text-sm font-medium text-gray-700">
                  Species *
                </Label>
                <Select
                  value={formData.species}
                  onValueChange={(value) => handleInputChange('species', value)}
                >
                  <SelectTrigger className={errors.species ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select species" />
                  </SelectTrigger>
                  <SelectContent>
                    {speciesOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.species && (
                  <p className="text-red-600 text-sm">{errors.species}</p>
                )}
              </div>

              {/* Breed */}
              <div className="space-y-2">
                <Label htmlFor="breed" className="text-sm font-medium text-gray-700">
                  Breed
                </Label>
                <Input
                  id="breed"
                  value={formData.breed || ''}
                  onChange={(e) => handleInputChange('breed', e.target.value)}
                  placeholder="Enter breed"
                />
              </div>

              {/* Birth Date */}
              <div className="space-y-2">
                <Label htmlFor="birth_date" className="text-sm font-medium text-gray-700">
                  Birth Date
                </Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={formData.birth_date || ''}
                  onChange={(e) => handleInputChange('birth_date', e.target.value)}
                  className={errors.birth_date ? 'border-red-500' : ''}
                />
                {errors.birth_date && (
                  <p className="text-red-600 text-sm">{errors.birth_date}</p>
                )}
              </div>

              {/* Weight */}
              <div className="space-y-2">
                <Label htmlFor="weight" className="text-sm font-medium text-gray-700">
                  Weight (kg)
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={formData.weight || ''}
                  onChange={(e) => handleInputChange('weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="Enter weight"
                  className={errors.weight ? 'border-red-500' : ''}
                />
                {errors.weight && (
                  <p className="text-red-600 text-sm">{errors.weight}</p>
                )}
              </div>

              {/* Color */}
              <div className="space-y-2">
                <Label htmlFor="color" className="text-sm font-medium text-gray-700">
                  Color
                </Label>
                <Input
                  id="color"
                  value={formData.color || ''}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  placeholder="Enter color"
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Microchip Number */}
              <div className="space-y-2">
                <Label htmlFor="microchip_number" className="text-sm font-medium text-gray-700">
                  Microchip Number
                </Label>
                <Input
                  id="microchip_number"
                  value={formData.microchip_number || ''}
                  onChange={(e) => handleInputChange('microchip_number', e.target.value)}
                  placeholder="Enter microchip number"
                />
              </div>

              {/* Registration Number */}
              <div className="space-y-2">
                <Label htmlFor="registration_number" className="text-sm font-medium text-gray-700">
                  Registration Number
                </Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number || ''}
                  onChange={(e) => handleInputChange('registration_number', e.target.value)}
                  placeholder="Enter registration number"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium text-gray-700">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Enter any additional notes about your pet"
                rows={3}
              />
            </div>

            {/* Guardian Information Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pet Guardian Information</h3>
              <p className="text-sm text-gray-600 mb-4">
                Designate someone to care for your pet if something happens to you.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="guardian_name" className="text-sm font-medium text-gray-700">
                    Guardian Name
                  </Label>
                  <Input
                    id="guardian_name"
                    value={formData.guardian_name || ''}
                    onChange={(e) => handleInputChange('guardian_name', e.target.value)}
                    placeholder="Enter guardian's full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guardian_relationship" className="text-sm font-medium text-gray-700">
                    Relationship
                  </Label>
                  <Input
                    id="guardian_relationship"
                    value={formData.guardian_relationship || ''}
                    onChange={(e) => handleInputChange('guardian_relationship', e.target.value)}
                    placeholder="e.g., Family member, Friend, Neighbor"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="guardian_phone" className="text-sm font-medium text-gray-700">
                    Phone Number
                  </Label>
                  <Input
                    id="guardian_phone"
                    value={formData.guardian_phone || ''}
                    onChange={(e) => handleInputChange('guardian_phone', e.target.value)}
                    placeholder="Enter guardian's phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="guardian_email" className="text-sm font-medium text-gray-700">
                    Email Address
                  </Label>
                  <Input
                    id="guardian_email"
                    type="email"
                    value={formData.guardian_email || ''}
                    onChange={(e) => handleInputChange('guardian_email', e.target.value)}
                    placeholder="Enter guardian's email address"
                  />
                </div>
              </div>

              <div className="mt-6">
                <Label htmlFor="guardian_address" className="text-sm font-medium text-gray-700">
                  Address
                </Label>
                <Textarea
                  id="guardian_address"
                  value={formData.guardian_address || ''}
                  onChange={(e) => handleInputChange('guardian_address', e.target.value)}
                  placeholder="Enter guardian's full address"
                  rows={2}
                />
              </div>

              <div className="mt-6">
                <Label htmlFor="guardian_notes" className="text-sm font-medium text-gray-700">
                  Guardian Notes
                </Label>
                <Textarea
                  id="guardian_notes"
                  value={formData.guardian_notes || ''}
                  onChange={(e) => handleInputChange('guardian_notes', e.target.value)}
                  placeholder="Any special instructions or notes for the guardian"
                  rows={3}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Create Pet
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
} 